// Extrait du Dashboard Finance — graphique d'évolution mensuelle des
// factures et dossiers, avec sélecteur d'année.
// Données : /api/metier/stats/dashboard-commercial
import { useMemo, useState } from "react";
import { BarChart3, Loader2 } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { YearSelector } from "../../components/ui/year-selector";
import { useFinanceFetch } from "./useFinanceFetch";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-xl border"
      style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
    >
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ color: e.color }}>
          {e.name}: <span className="font-bold">{e.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function FinanceEvolutionWidget() {
  const { data, loading, error } = useFinanceFetch("/api/metier/stats/dashboard-commercial");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const availableYears = useMemo(() => {
    if (!data?.evolution_mensuelle) return [new Date().getFullYear().toString()];
    return [...new Set(data.evolution_mensuelle.map((m) => m.mois.slice(0, 4)))].sort();
  }, [data]);

  const evolutionData = useMemo(() => {
    if (!data?.evolution_mensuelle) return [];
    return data.evolution_mensuelle
      .filter((m) => m.mois.startsWith(selectedYear))
      .map((m) => ({
        ...m,
        label: new Date(m.mois + "-01").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
      }));
  }, [data, selectedYear]);

  if (loading) {
    return (
      <div
        className="rounded-xl p-5 h-full flex items-center justify-center"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      >
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="rounded-xl p-5"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
      >
        Erreur Évolution mensuelle : {error}
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-5 h-full"
      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      data-testid="finance-evolution-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
          <BarChart3 className="w-4 h-4" style={{ color: "#8b5cf6" }} />
          Évolution mensuelle
        </h3>
        <YearSelector
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          availableYears={availableYears}
          accentColor="#8b5cf6"
        />
      </div>
      {evolutionData.length > 0 ? (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={evolutionData}>
            <defs>
              <linearGradient id="gradFactEvo" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--oh-border)" />
            <XAxis dataKey="label" tick={{ fill: "var(--oh-text-muted)", fontSize: 11 }} />
            <YAxis tick={{ fill: "var(--oh-text-muted)", fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top" align="right" iconType="line" iconSize={14}
              wrapperStyle={{ fontSize: "12px", color: "var(--oh-text-muted)", paddingBottom: "8px" }}
            />
            <Area type="monotone" dataKey="factures" name="Factures" stroke="#10b981" fill="url(#gradFactEvo)" strokeWidth={2} />
            <Area type="monotone" dataKey="dossiers" name="Dossiers" stroke="var(--oh-stat-blue-text)" fill="none" strokeWidth={1} strokeDasharray="5 5" />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex items-center justify-center h-48" style={{ color: "var(--oh-text-muted)" }}>
          Pas de données
        </div>
      )}
    </div>
  );
}
